import { bandKey, criteriaDifferences, differenceKey, type CriteriaDifference } from './criteriaDiff';
import type { GradeBand, Outcome, RuleSet } from './settingsApi';

export type CriteriaCondition = { measure: string; value: string; mark?: string; removed?: boolean };
export type CriteriaRow = {
  outcome: Outcome;
  label: 'Exits' | 'Eligible for review';
  review: boolean;
  conditions: CriteriaCondition[];
  note?: string;
};
export type CriteriaGroup = { key: string; label: string; alternate: boolean; noExit: boolean; rows: CriteriaRow[] };

const GRADE_ORDER = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const ACCESS_MEASURES: Record<string, string> = {
  overall: 'Overall composite',
  literacy: 'Literacy composite',
  oral: 'Oral composite',
  comprehension: 'Comprehension composite',
  listening: 'Listening',
  speaking: 'Speaking',
  reading: 'Reading',
  writing: 'Writing',
};

export function measureName(measure: string, alternate: boolean): string {
  const named = ACCESS_MEASURES[measure] ?? measure;
  return alternate ? named.replace(' composite', '') : named;
}

export function formatMinimum(minimum: number, alternate: boolean): string {
  return alternate ? String(Math.round(minimum)) : minimum.toFixed(1);
}

function gradeAt(index: number): string {
  return GRADE_ORDER[index] ?? '';
}

function gradeRuns(grades: string[]): string {
  const ordered = GRADE_ORDER.map((g, i) => (grades.includes(g) ? i : -1)).filter((i) => i >= 0);
  const runs: string[] = [];
  let start = -1;
  let prev = -1;
  for (const i of ordered) {
    if (start < 0) {
      start = i;
    } else if (i !== prev + 1) {
      runs.push(start === prev ? gradeAt(start) : `${gradeAt(start)}–${gradeAt(prev)}`);
      start = i;
    }
    prev = i;
  }
  if (start >= 0) runs.push(start === prev ? gradeAt(start) : `${gradeAt(start)}–${gradeAt(prev)}`);
  return runs.join(', ');
}

function gradesLabel(grades: 'all' | string[]): string {
  if (grades === 'all' || grades.length === 0) return 'All grades';
  if (grades.length === 1 && grades[0] === 'K') return 'Kindergarten';
  const runs = gradeRuns(grades);
  return `${grades.length === 1 ? 'Grade' : 'Grades'} ${runs}`;
}

export function bandLabel(band: GradeBand): string {
  if (band.assessment !== 'alternate_access') return gradesLabel(band.grades);
  const all = band.grades === 'all' || band.grades.length === 0;
  return all ? 'Alternate ACCESS · 1–5 scale' : `Alternate ACCESS · ${gradesLabel(band.grades)} · 1–5 scale`;
}

function firstGradeIndex(grades: 'all' | string[]): number {
  if (grades === 'all' || grades.length === 0) return -1;
  return Math.min(...grades.map((g) => GRADE_ORDER.indexOf(g)));
}

function rowFrom(rule: RuleSet, alternate: boolean): CriteriaRow {
  const review = rule.outcome === 'eligible_for_review';
  const row: CriteriaRow = {
    outcome: rule.outcome,
    label: review ? 'Eligible for review' : 'Exits',
    review,
    conditions: rule.conditions.map((c) => ({
      measure: measureName(c.measure, alternate),
      value: formatMinimum(c.minimum, alternate),
    })),
  };
  if (rule.displayNote) row.note = rule.displayNote;
  return row;
}

function orderedBands(bands: GradeBand[]): { band: GradeBand; index: number }[] {
  return bands
    .map((band, index) => ({ band, index }))
    .sort((a, b) => {
      const altA = a.band.assessment === 'alternate_access' ? 1 : 0;
      const altB = b.band.assessment === 'alternate_access' ? 1 : 0;
      if (altA !== altB) return altA - altB;
      const gradeA = firstGradeIndex(a.band.grades);
      const gradeB = firstGradeIndex(b.band.grades);
      if (gradeA !== gradeB) return gradeA - gradeB;
      return a.index - b.index;
    });
}

export function orderBands(bands: GradeBand[]): GradeBand[] {
  return orderedBands(bands).map(({ band }) => band);
}

const OUTCOME_ORDER: Outcome[] = ['exits', 'eligible_for_review'];

export function criteriaGroups(bands: GradeBand[]): CriteriaGroup[] {
  return orderedBands(bands).map(({ band, index }) => {
    const alternate = band.assessment === 'alternate_access';
    const rows = [...band.ruleSets]
      .sort((a, b) => (a.outcome === b.outcome ? 0 : a.outcome === 'exits' ? -1 : 1))
      .map((rule) => rowFrom(rule, alternate));
    return { key: `${band.assessment}-${index}`, label: bandLabel(band), alternate, noExit: band.noExit, rows };
  });
}

const ADDED_MARK = 'Added by your district';
const REMOVED_MARK = 'Removed by your district';

function markFor(difference: CriteriaDifference | undefined, alternate: boolean, stateName: string): string | undefined {
  if (!difference) return undefined;
  if (difference.kind === 'changed') return `${stateName}’s record: ${formatMinimum(difference.recordMinimum ?? 0, alternate)}`;
  return ADDED_MARK;
}

function rowAgainstRecord(outcome: Outcome, district: RuleSet | undefined, record: RuleSet | undefined, alternate: boolean, key: string, marks: Map<string, CriteriaDifference>, stateName: string): CriteriaRow {
  const review = outcome === 'eligible_for_review';
  const conditions: CriteriaCondition[] = (district?.conditions ?? []).map((c) => {
    const mark = markFor(marks.get(differenceKey(key, outcome, c.measure)), alternate, stateName);
    return { measure: measureName(c.measure, alternate), value: formatMinimum(c.minimum, alternate), ...(mark ? { mark } : {}) };
  });
  for (const c of record?.conditions ?? []) {
    if (district?.conditions.some((d) => d.measure === c.measure)) continue;
    conditions.push({ measure: measureName(c.measure, alternate), value: formatMinimum(c.minimum, alternate), mark: REMOVED_MARK, removed: true });
  }
  const note = district?.displayNote ?? record?.displayNote;
  return { outcome, label: review ? 'Eligible for review' : 'Exits', review, conditions, ...(note ? { note } : {}) };
}

export function criteriaGroupsAgainstRecord(effective: GradeBand[], record: GradeBand[], stateName: string): CriteriaGroup[] {
  const marks = new Map(criteriaDifferences(record, effective).map((d) => [d.key, d]));
  const recordByKey = new Map(record.map((b) => [bandKey(b), b]));
  const districtKeys = new Set(effective.map((b) => bandKey(b)));
  const dropped = record.filter((b) => !districtKeys.has(bandKey(b)));
  return orderedBands([...effective, ...dropped]).map(({ band, index }) => {
    const alternate = band.assessment === 'alternate_access';
    const key = bandKey(band);
    const districtBand = districtKeys.has(key) ? band : undefined;
    const recordBand = recordByKey.get(key);
    const rows = OUTCOME_ORDER.flatMap((outcome) => {
      const district = districtBand?.ruleSets.find((r) => r.outcome === outcome);
      const fromRecord = recordBand?.ruleSets.find((r) => r.outcome === outcome);
      if (!district && !fromRecord) return [];
      return [rowAgainstRecord(outcome, district, fromRecord, alternate, key, marks, stateName)];
    });
    return { key: `${band.assessment}-${index}`, label: bandLabel(band), alternate, noExit: band.noExit, rows };
  });
}

export function criteriaDifferenceCount(effective: GradeBand[], record: GradeBand[]): number {
  return criteriaDifferences(record, effective).length;
}

export function schoolYearLabel(schoolYear: string): string {
  return schoolYear.replace('-', '–');
}

export function versionLine(version: { effectiveFrom: string; verifiedOn: string }): string {
  const guidance = `${schoolYearLabel(version.effectiveFrom)} guidance`;
  const verified = new Date(version.verifiedOn);
  if (Number.isNaN(verified.getTime())) return guidance;
  const month = verified.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' });
  return `${guidance} · verified ${month}`;
}
